import { Box, FormControlLabel, Grid, Radio, RadioGroup, Tooltip, Typography } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { StandardDialog, TextField, Button, ConfirmDialog, SelectField } from "@roc/ui";
import { useLenderPortalStore } from "apps/lender-portal/src/app/hooks/useLenderPortalStore";
import { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { ConfirmationButton } from "@roc/ui";
import { observer } from "mobx-react";
import { formatDate, isNil } from "@roc/feature-utils";
import { Document } from "@roc/feature-types";
import { CustomTaskDialog } from "@roc/feature-general-tasks";
import { AchConnectionGuide, AchLinkStatus, useAchConnectionGuide } from "@roc/feature-plaid";
import { HelpOutline } from "@material-ui/icons";

export type ACHFormProps = {
  open?: boolean;
  onClose?: () => void;
  loan: any;
  document: Document;
};

const when = (iso: string) => formatDate(iso, 'MMM d, yyyy h:mm a');

/** "Plaid link" / "manual-entry link", or just "link" for one made before links had a mode. */
const linkKind = (link: AchLinkStatus) =>
  link?.entryMode === 'PLAID' ? 'Plaid link' : link?.entryMode === 'MANUAL' ? 'manual-entry link' : 'link';

/** What happened to the loan's newest link. The URL itself is never shown again: only a hash of it is stored. */
const linkStatusText = (link: AchLinkStatus, borrowerName: string) => {
  if (!link) return 'No link has been created yet.';
  const who = borrowerName ?? 'the borrower';
  const kind = linkKind(link);
  switch (link.status) {
    case 'ACTIVE':
      return `The ${kind} for ${who} was created on ${when(link.createdDate)} and expires on ${when(link.expiresAt)}. `
        + (link.lastOpenedAt ? `Last opened on ${when(link.lastOpenedAt)}.` : 'It has not been opened yet.')
        // Links created before links could be copied again have no stored copy.
        + (link.canCopy ? '' : ' This link can\'t be copied again; create a new link to share it.');
    case 'LOCKED':
      return `The ${kind} for ${who} is locked after too many wrong codes. Create a new link to continue.`;
    case 'COMPLETED':
      return `${borrowerName ?? 'The borrower'} completed the ACH form on ${when(link.completedAt)}.`;
    case 'EXPIRED':
      return `The last ${kind} for ${who} expired on ${when(link.expiresAt)}.`;
    case 'REVOKED':
      return `The last ${kind} for ${who} was cancelled.`;
    default:
      return '';
  }
};

/** Info while the link is open, success once signed, warning once it can no longer be used. */
const alertSeverity = (link: AchLinkStatus): 'info' | 'success' | 'warning' => {
  if (link?.status === 'COMPLETED') return 'success';
  if (link?.status === 'EXPIRED' || link?.status === 'REVOKED' || link?.status === 'LOCKED') return 'warning';
  return 'info';
};

/** Shown before Create Link or Send Email replaces a link that is still open. */
const ReplaceWarning = ({ link, borrowerName, action }: { link: AchLinkStatus; borrowerName?: string; action: 'create' | 'email' }) => {
  const who = borrowerName ?? 'Your borrower';
  return (
    <Box>
      <Typography gutterBottom>
        {who} already has an open link, created on {when(link.createdDate)}
        {link.lastOpenedAt ? ` and last opened on ${when(link.lastOpenedAt)}` : ''}.
      </Typography>
      <Typography gutterBottom>
        <strong>
          {action === 'email' ? 'Sending a new email' : 'Creating a new link'} cancels that link right away. The link
          {borrowerName ? ` ${borrowerName}` : ' your borrower'} already has will stop working
          {link.lastOpenedAt ? ', including if they are filling in the form right now' : ''}.
        </strong>
      </Typography>
      <Typography color="textSecondary">
        To share the same link again, go back and use Copy link instead.
      </Typography>
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  instruction: {
    fontWeight: 600,
  },
  underline: {
    fontWeight: 600,
    textDecorationLine: 'underline',
  },
  tooltip: {
    whiteSpace: 'pre-line',
    fontSize: theme.typography.pxToRem(14),
    maxWidth: '200px',
  },
}));

export const ACHFormDialog = observer((props: ACHFormProps) => {
  const { achFormStore } = useLenderPortalStore();
  const [emailMode, setEmailMode] = useState<'PLAID' | 'MANUAL'>('PLAID');
  // Opening the dialog only reads: a link is created when the lender asks for one, since every new link cancels the
  // one the borrower already has.
  useEffect(() => {
    if (props.open && !isNil(props.loan)) {
      achFormStore.reset();
      achFormStore.getLoanACHData(props.loan.loanId, props.loan);
      achFormStore.fetchACHLinkStatus(props.loan.loanId);
      setEmailMode('PLAID');
    }
  }, [props.open, props.loan]);

  const linkBorrowerName = () => {
    const borrowerId = achFormStore.achLinkStatus?.borrowerId;
    const borrower = props.loan?.borrowers?.find(b => b.borrowerId === borrowerId);
    return borrower?.fullName;
  };
  const completed = achFormStore.achLinkStatus?.status === 'COMPLETED';
  // A cancelled link only remains after the data reset, which starts the loan again: it reads as having no link.
  const shownLink = achFormStore.achLinkStatus?.status === 'REVOKED' ? null : achFormStore.achLinkStatus;
  const cannotIssue = isNil(achFormStore.selectedBorrower) || achFormStore.issuingLink;

  const classes = useStyles();

  // The guide opens only when the help button is clicked, never by itself.
  const guide = useAchConnectionGuide('staff', false);

  // Plaid is on for the loan only when the global plaidLinkFeature flag AND the loan's own flag (loanClosingData.
  // plaidAchEnabled: true for loans on the Plaid process, false for older ones) are on. Otherwise the only link is the
  // manual-entry one, under the old "Create Link" label.
  const userFeatures = achFormStore.globalStore?.userFeatures;
  const plaidOn = userFeatures?.plaidLinkFeature !== false && props.loan?.loanClosingData?.plaidAchEnabled === true;
  const canReset = !!userFeatures?.allowACHReset;
  const createLink = () =>
    plaidOn ? achFormStore.getACHLink(props.loan.loanId) : achFormStore.getManualACHLink(props.loan.loanId);
  // The email carries the Plaid link, or the manual-entry one: always manual with Plaid off; with it on, staff choose
  // (the manual-entry link is offered once a link exists, like the Create Manual Link button).
  const canChooseEmailLink = plaidOn && achFormStore.canCreateManualLink;
  const emailManual = !plaidOn || (canChooseEmailLink && emailMode === 'MANUAL');
  const emailLinkChoice = canChooseEmailLink && (
    <Box mt={2}>
      <Typography gutterBottom className={classes.instruction}>Which link should the email carry?</Typography>
      <RadioGroup value={emailMode} onChange={event => setEmailMode(event.target.value as 'PLAID' | 'MANUAL')}>
        <FormControlLabel value="PLAID" control={<Radio color="primary" />} label="Plaid link: the borrower connects the bank" />
        <FormControlLabel value="MANUAL" control={<Radio color="primary" />} label="Manual-entry link: the borrower types the bank details" />
      </RadioGroup>
    </Box>
  );

  const linkUrlField = achFormStore.achUrl && (
    <Grid item container justifyContent="flex-start" spacing={2}>
      <Grid item xs={12}>
        <TextField
          testId="achUrl"
          variant="outlined"
          value={achFormStore.achUrl}
          helperText="You can copy this link again any time while it's open, using Copy link above."
          fullWidth
          disabled
        />
      </Grid>
    </Grid>
  );

  const getContent = () => {
    return (
      <>
        <Grid container justifyContent="center">
          {(
            <Grid
              alignItems="center"
              container
              item
              justifyContent="center"
              xs={12}
            >
              <Grid item container xs={11} spacing={2}>
                <Grid item container alignItems="center" spacing={2}>
                  <Grid item xs={12} container spacing={2}>
                    <Grid item xs={12}>
                      <Typography>
                        To facilitate a smooth and efficient closing, an authorized signatory borrower must complete the ACH consent
                        process as part of underwriting. This should take your borrower no more than 5 minutes.
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography>
                        The consent process is accessed through a link shared with your borrower. You can copy
                        and send the link to your borrower manually, or you can easily send the link to your
                        borrower in an email via the portal.
                      </Typography>
                    </Grid>
                    {(plaidOn || canReset) && (
                      <Grid item xs={12}>
                        <Box display="flex" flexWrap="wrap" style={{ gap: 12 }}>
                          {plaidOn && (
                            <Button
                              testId="achGuideOpen"
                              variant="outlined"
                              size="small"
                              color="primary"
                              unsetTextTransfrom
                              startIcon={<HelpOutline />}
                              onClick={guide.show}
                            >
                              How bank connection works
                            </Button>
                          )}
                          {canReset && (
                            <Tooltip title="Put this loan's ACH data back to the start so the form can be done again. Staff only." arrow placement="right" classes={{ tooltip: classes.tooltip, }}>
                              <div>
                                <ConfirmationButton
                                  variant="outlined"
                                  size="small"
                                  color="primary"
                                  unsetTextTransfrom
                                  onClick={() => achFormStore.resetACHData(props.loan.loanId)}
                                  testId="resetACHData"
                                  disabled={achFormStore.resettingData}
                                  confirmDialogProps={{
                                    title: 'Reset the ACH data?',
                                    body: (
                                      <Box>
                                        <Typography gutterBottom>
                                          This puts the loan back to before the bank account was linked: the linked bank account is
                                          removed, the saved ACH data is deleted, the open ACH links stop working and the ACH Consent
                                          Form task goes back to Pending without its documents.
                                        </Typography>
                                        <Typography>
                                          <strong>This can't be undone.</strong> The borrower will have to do the ACH form again.
                                        </Typography>
                                      </Box>
                                    ),
                                    cancelButtonText: 'Go Back',
                                    confirmButtonText: 'Reset ACH data'
                                  }}
                                >
                                  RESET ACH DATA
                                </ConfirmationButton>
                              </div>
                            </Tooltip>
                          )}
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                {achFormStore.borrowerOptions.length === 0 &&
                  < Grid item xs={12}>
                    <Typography color="error">
                      Please make sure there is at least one authorized signatory borrower for this action.
                    </Typography>
                  </Grid>
                }
                {achFormStore.borrowerOptions.length > 0 &&
                  <>
                    {achFormStore.borrowerOptions.length === 1 ? (
                      <>
                        <Grid item lg={12} >
                          <TextField
                            testId="selectedBorrower"
                            variant="outlined"
                            standaloneLabel
                            label="Authorized Signatory Borrower"
                            value={achFormStore.currentAuthSignatoryBorrower.fullName}
                            disabled={true}
                            fullWidth
                          />
                        </Grid>
                        <Grid item lg={12} >
                          <TextField
                            testId="selectedBorrower"
                            variant="outlined"
                            standaloneLabel
                            value={achFormStore.currentAuthSignatoryBorrower.emailAddress}
                            disabled={true}
                            fullWidth
                          />
                        </Grid>

                      </>
                    ) : (
                      <>
                        <Grid item xs={12} sm>
                          <SelectField
                            standaloneLabel
                            name={'selectedBorrower'}
                            value={achFormStore.selectedBorrower}
                            options={achFormStore.borrowerOptions}
                            onChange={value => achFormStore.setSelectedBorrower(value)}
                            variant="outlined"
                            fullWidth
                            shrink
                            required
                            testId="selectedBorrower"
                          />
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12}>
                      <Alert
                        severity={alertSeverity(shownLink)}
                        action={achFormStore.achLinkStatus?.canCopy && (
                          <Button
                            testId="copyACHLink"
                            color="inherit"
                            size="small"
                            onClick={() => achFormStore.copyACHLink(props.loan.loanId)}
                            disabled={achFormStore.issuingLink}
                          >
                            COPY LINK
                          </Button>
                        )}
                      >
                        {linkStatusText(shownLink, linkBorrowerName())}
                      </Alert>
                    </Grid>
                    {linkUrlField}
                    {!completed &&
                      <Grid item container justifyContent="flex-start" spacing={2}>
                        <Grid item xs={12}>
                          <Typography className={classes.instruction}>
                            Select Link Delivery Method
                          </Typography>
                        </Grid>
                        <Grid item xs={4} sm={3}>
                          <Tooltip title="Create a new link, then copy it and share it with your borrower manually. Any open link stops working." arrow placement="left" classes={{ tooltip: classes.tooltip, }}>
                            <div>
                              {achFormStore.hasOpenLink ? (
                                <ConfirmationButton
                                  variant="contained"
                                  color="secondary"
                                  onClick={createLink}
                                  testId="createACHLink"
                                  disabled={cannotIssue}
                                  confirmDialogProps={{
                                    title: 'Replace the current link?',
                                    body: <ReplaceWarning link={achFormStore.achLinkStatus} borrowerName={linkBorrowerName()} action="create" />,
                                    cancelButtonText: 'Keep current link',
                                    confirmButtonText: 'Create new link'
                                  }}
                                  fullWidth
                                >
                                  CREATE LINK
                                </ConfirmationButton>
                              ) : (
                                <Button
                                  variant="contained"
                                  color="secondary"
                                  onClick={createLink}
                                  testId="createACHLink"
                                  disabled={cannotIssue}
                                  fullWidth
                                >
                                  CREATE LINK
                                </Button>
                              )}
                            </div>
                          </Tooltip>
                        </Grid>
                        <Grid item xs={4} sm={3}>
                          <Tooltip title="Automatically email a new link to your borrower" arrow placement="right" classes={{ tooltip: classes.tooltip, }}>
                            <div>
                              <ConfirmationButton
                                variant="contained"
                                onClick={() => achFormStore.sendACHEmail(props.loan.loanId, emailManual)}
                                color="primary"
                                testId="sendACHEmail"
                                disabled={cannotIssue}
                                confirmDialogProps={achFormStore.hasOpenLink ? {
                                  title: 'Email a new link?',
                                  body: (
                                    <>
                                      <ReplaceWarning link={achFormStore.achLinkStatus} borrowerName={linkBorrowerName()} action="email" />
                                      {emailLinkChoice}
                                    </>
                                  ),
                                  cancelButtonText: 'Keep current link',
                                  confirmButtonText: 'Send new link'
                                } : {
                                  title: 'Confirmation',
                                  body: (
                                    <>
                                      <Typography>
                                        {plaidOn
                                          ? 'This will send an email containing the link to the ACH consent process to your borrower.'
                                          : 'This will send an email containing a link where your borrower types the bank details.'}
                                      </Typography>
                                      {emailLinkChoice}
                                    </>
                                  ),
                                  cancelButtonText: 'Go Back',
                                  confirmButtonText: 'Ok'
                                }}
                                fullWidth
                              >
                                Send Email
                              </ConfirmationButton>
                            </div>
                          </Tooltip>
                        </Grid>
                        {plaidOn && achFormStore.canCreateManualLink && (
                          <Grid item xs={4} sm={3}>
                            <Tooltip title="Create a new link where the borrower types the bank details instead of connecting the bank. Use it when connecting the bank doesn't work for them. Any open link stops working." arrow placement="right" classes={{ tooltip: classes.tooltip, }}>
                              <div>
                                {achFormStore.hasOpenLink ? (
                                  <ConfirmationButton
                                    variant="contained"
                                    color="secondary"
                                    onClick={() => achFormStore.getManualACHLink(props.loan.loanId)}
                                    testId="createManualACHLink"
                                    disabled={cannotIssue}
                                    confirmDialogProps={{
                                      title: 'Replace the current link with a manual-entry link?',
                                      body: <ReplaceWarning link={achFormStore.achLinkStatus} borrowerName={linkBorrowerName()} action="create" />,
                                      cancelButtonText: 'Keep current link',
                                      confirmButtonText: 'Create manual link'
                                    }}
                                    fullWidth
                                  >
                                    CREATE MANUAL LINK
                                  </ConfirmationButton>
                                ) : (
                                  <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={() => achFormStore.getManualACHLink(props.loan.loanId)}
                                    testId="createManualACHLink"
                                    disabled={cannotIssue}
                                    fullWidth
                                  >
                                    CREATE MANUAL LINK
                                  </Button>
                                )}
                              </div>
                            </Tooltip>
                          </Grid>
                        )}
                      </Grid>
                    }
                  </>
                }
                <>

                </>
              </Grid>
            </Grid>
          )}
        </Grid>
      </>
    );
  }

  return (
    <>
      {props.document?.taskId ? (
        <CustomTaskDialog
          open={props.open}
          title="ACH Consent"
          handleClose={props.onClose}
          dialogContent={getContent()}
          taskId={props.document?.taskId}
        />
      ) : (
        <StandardDialog
          open={props.open}
          title="ACH Consent"
          maxWidth="md"
          handleClose={props.onClose}
          dialogContent={getContent()}
        />
      )}
      <AchConnectionGuide variant="staff" open={guide.open} onClose={guide.close} />
    </>

  );
});